import { selectCurrentPoc as selectCurrentPocShared } from '@prism-ehr/shared/utils/planOfCareStatus';
import { getAuthCaseIds } from './priorAuthHelpers';

const DEFAULT_THRESHOLD = 2480;

/**
 * Structured prior-auth alert for a patient's auths: `{ message, kind, used,
 * authorized, expiration_date, payer, priority }` or null. `message` is the
 * legacy one-line string; `used`/`authorized` are set for visit-based auths so
 * surfaces can show the cap explicitly.
 */
export function getPriorAuthAlert(auths) {
  const message = getPriorAuthViolation(auths);
  if (!message) return null;
  return { ...(lastAlertDetail || {}), message, kind: 'prior_auth' };
}

// Detail captured by the most recent getPriorAuthViolation() pick
let lastAlertDetail = null;

export function getPriorAuthViolation(auths) {
  lastAlertDetail = null;
  if (!auths?.length) return null;
  const now = Date.now();

  // An auth linked to several cases (case_ids) counts for every one of them.
  const casesWithActiveAuth = new Set();
  for (const auth of auths) {
    if (auth.status === 'approved' || auth.status === 'partially_approved') {
      if (!auth.expiration_date || Math.ceil((auth.expiration_date - now) / 86400000) > 0) {
        getAuthCaseIds(auth).forEach(id => casesWithActiveAuth.add(id));
      }
    }
  }

  // A sibling auth sharing a case with visits still available supersedes
  // this auth's exhaustion warning — the sibling's own alerts tell the story
  const hasSiblingAuthWithCapacity = (auth) => {
    const mine = getAuthCaseIds(auth);
    return auths.some(a =>
      a.id !== auth.id &&
      getAuthCaseIds(a).some(id => mine.includes(id)) &&
      (a.status === 'approved' || a.status === 'partially_approved') &&
      a.auth_type === 'visit_based' && a.visits_authorized &&
      (a._visits_used_auto ?? 0) < a.visits_authorized
    );
  };

  let best = null;
  let bestPriority = Infinity;

  for (const auth of auths) {
    const payer = auth.payer_name || 'Unknown payer';

    if (auth.status === 'denied' && bestPriority > 1) {
      best = `Prior auth denied — ${payer}`;
      bestPriority = 1;
      lastAlertDetail = { payer, priority: 1, status: auth.status };
      continue;
    }

    if (auth.expiration_date && (auth.status === 'approved' || auth.status === 'partially_approved')) {
      const daysLeft = Math.ceil((auth.expiration_date - now) / 86400000);
      if (daysLeft <= 0 && bestPriority > 2) {
        if (getAuthCaseIds(auth).some(id => casesWithActiveAuth.has(id))) continue;
        best = `Prior auth expired — ${payer}`;
        bestPriority = 2;
        lastAlertDetail = { payer, priority: 2, status: auth.status, expiration_date: auth.expiration_date };
      } else if (daysLeft <= 7 && daysLeft > 0 && bestPriority > 4) {
        best = `Prior auth expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''} — ${payer}`;
        bestPriority = 4;
        lastAlertDetail = { payer, priority: 4, status: auth.status, expiration_date: auth.expiration_date };
      } else if (daysLeft <= 30 && daysLeft > 7 && bestPriority > 6) {
        best = `Prior auth expiring soon — ${payer}`;
        bestPriority = 6;
        lastAlertDetail = { payer, priority: 6, status: auth.status, expiration_date: auth.expiration_date };
      }
    }

    if (auth.auth_type === 'visit_based' && auth.visits_authorized && (auth.status === 'approved' || auth.status === 'partially_approved')) {
      if (hasSiblingAuthWithCapacity(auth)) continue;
      const used = auth._visits_used_auto ?? 0;
      const pct = (used / auth.visits_authorized) * 100;
      if (pct >= 90 && bestPriority > 3) {
        best = `${used}/${auth.visits_authorized} authorized visits used — ${payer}`;
        bestPriority = 3;
        lastAlertDetail = { payer, priority: 3, status: auth.status, used, authorized: auth.visits_authorized, expiration_date: auth.expiration_date };
      } else if (pct >= 80 && bestPriority > 5) {
        best = `${used}/${auth.visits_authorized} authorized visits used — ${payer}`;
        bestPriority = 5;
        lastAlertDetail = { payer, priority: 5, status: auth.status, used, authorized: auth.visits_authorized, expiration_date: auth.expiration_date };
      }
    }
  }

  return best;
}

// The POC whose certification currently governs a case — canonical rule
// lives in @prism-ehr/shared/utils/planOfCareStatus. Alert callers require
// a certification_end so a plan without a cert window can never drive
// (or suppress) an expiration alert.
export function selectCurrentPoc(pocs) {
  return selectCurrentPocShared(pocs, { requireCertEnd: true });
}

/**
 * Whether the case's CURRENT live POC has actually expired (certification_end
 * in the past). Same case/POC selection as getPocExpirationViolation; used
 * for the PN-due calendar color so it matches the chart's expired banner
 * without depending on a billing rule.
 */
export function isPocExpired(pocs) {
  if (!pocs?.length) return false;
  const caseRecord = pocs.find(p => p.case)?.case;
  if (!caseRecord || caseRecord.status !== 'active') return false;
  const poc = selectCurrentPoc(pocs);
  if (!poc?.certification_end) return false;
  const endMs = poc.certification_end > 1e12 ? poc.certification_end : poc.certification_end * 1000;
  return Math.ceil((endMs - Date.now()) / 86400000) <= 0;
}

export function getPocExpirationViolation(pocs) {
  if (!pocs?.length) return null;

  // Only active cases need recertification; discharged/held cases keep their
  // POCs silently. Orphan POCs (no case row attached) never alert.
  const caseRecord = pocs.find(p => p.case)?.case;
  if (!caseRecord || caseRecord.status !== 'active') return null;

  const poc = selectCurrentPoc(pocs);
  if (!poc) return null;

  const endMs = poc.certification_end > 1e12 ? poc.certification_end : poc.certification_end * 1000;
  const daysLeft = Math.ceil((endMs - Date.now()) / 86400000);

  if (daysLeft <= 0) return 'Plan of care expired';
  if (daysLeft <= 7) return `Plan of care expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
  if (daysLeft <= 30) return 'Plan of care expiring soon';
  return null;
}

export function getTherapyCapViolation(patient, kxThresholdOffset = 0) {
  if (!patient?.insurance) return null;
  const ins = Array.isArray(patient.insurance)
    ? (patient.insurance.find(i => i.is_primary) || patient.insurance[0])
    : patient.insurance;
  const ts = ins?.therapy_spending;
  if (!ts) return null;
  const offset = Number(kxThresholdOffset) > 0 ? Number(kxThresholdOffset) : 0;
  const threshold = offset > 0 ? Math.max(0, DEFAULT_THRESHOLD - offset) : (ts.kx_threshold || DEFAULT_THRESHOLD);
  const ptSlpExceeded = (ts.pt_slp?.amount || 0) > threshold;
  const otExceeded = (ts.ot?.amount || 0) > threshold;
  if (!ptSlpExceeded && !otExceeded) return null;
  const parts = [];
  if (ptSlpExceeded) parts.push('PT/SLP');
  if (otExceeded) parts.push('OT');
  return `${parts.join(' & ')} therapy cap exceeded — KX modifier required`;
}
